<template>
  <div class="traiding-wrapper">
    <div class="TVChartContainer" ref="chartContainer" />
  </div>
</template>

<script setup>
import {
  onMounted,
  onUnmounted,
  ref,
  defineProps,
  defineEmits,
  watch,
  computed,
  watchEffect,
  nextTick,
} from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
// import Datafeed from "./datafeed/datafeed";
// import BitgetDatafeed from "./datafeed/datafeed_bitget";
// import JpDatafeed from "./datafeed/datafeed_jp";
const { t, locale } = useI18n({ useScope: "global" });
const store = useStore();
const emit = defineEmits(["mounted"]);
const isDarkMode = computed(() => store.getters["displayed/isDarkMode"]);
const theme = computed(() => (isDarkMode.value ? "Dark" : "Light"));

const props = defineProps({
  symbol: {
    default: "Binance:BTC/USDT",
    type: String,
  },
  libraryPath: {
    default: "charting_library/", // 앞 슬래시 제거
    type: String,
  },
});

// let isChartReady = false;
const ticker = computed(() => store.state.transact.ticker);
const user_ref_id = computed(() => "refd2RsMDAwMDQzNQ==qwe"); // 테스트용
const chartContainer = ref();
let chartWidget = null;
let isScriptLoaded = false;
const countryCode = computed(() => store.state.transact.countryCode);
const changeDrawings = ref(false);
const transformTicker = computed(() => {
  if (!countryCode.value) return "";
  const value = ticker.value || "BTC";
  const exchange = ["US", "CN"].includes(countryCode.value) ? "bitget" : "Binance";
  return `${exchange}:${value}/USDT`;
});

const positionsList = computed(() => store.state.user_history.positionList);
const positions = computed(() => {
  let result = [];
  if (positionsList.value && positionsList.value.length > 0) {
    result = positionsList.value.filter((v) => v.symbol === ticker.value);
  }
  return result;
});
const openOrderList = computed(() => {
  let result = [];
  if (
    store.state.user_history.openOrderList &&
    store.state.user_history.openOrderList.length > 0
  ) {
    result = store.state.user_history.openOrderList.filter(
      (v) => v.symbol === ticker.value
    );
  }
  return result;
});
const lastPrice = computed(() => store.state.transact.lastPrice);

let drawings = [];

// 📦 Charting 라이브러리 동적 로딩
async function loadChartingLibrary() {
  if (isScriptLoaded) return;

  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector(
      `script[src='/${props.libraryPath}charting_library.js']`
    );
    if (existingScript) {
      isScriptLoaded = true;
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = `/${props.libraryPath}charting_library.js`;
    script.onload = async () => {
      isScriptLoaded = true;
      // 스크립트 로드 후 DOM 업데이트를 위한 대기
      await nextTick();
      await new Promise((resolve) => setTimeout(resolve, 200));
      resolve();
    };
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

// ⏳ TradingView 객체가 로드될 때까지 대기
const waitForTradingView = async () => {
  let attempts = 0;
  const maxAttempts = 50; // 5초 타임아웃 (100ms * 50)

  while (!window.TradingView && attempts < maxAttempts) {
    await new Promise((resolve) => setTimeout(resolve, 100));
    attempts++;
  }

  if (!window.TradingView) {
    throw new Error("TradingView failed to load");
  }
};

async function setTV() {
  // isChartReady = false;
  if (!chartContainer.value || !chartContainer.value.parentNode) {
    // console.log("Chart container not available");
    return;
  }

  if (!transformTicker.value) {
    // console.log("Transform ticker not available");
    return;
  }

  try {
    // 이전 차트 정리
    if (chartWidget) {
      chartWidget.remove();
      chartWidget = null;
      await new Promise((resolve) => setTimeout(resolve, 200));
    }

    // 컨테이너 초기화
    chartContainer.value.innerHTML = "";

    let DatafeedModule;
    if (["US", "CN"].includes(countryCode.value)) {
      DatafeedModule = async () => await import("./datafeed/datafeed_bitget");
    } else if (["JP"].includes(countryCode.value)) {
      DatafeedModule = async () => await import("./datafeed/datafeed_jp");
    } else {
      DatafeedModule = async () => await import("./datafeed/datafeed");
    }

    const datafeed = await DatafeedModule();

    await nextTick();

    // console.log("Initializing chart with:", {
    //   container: !!chartContainer.value,
    //   parentNode: !!chartContainer.value?.parentNode,
    //   ticker: transformTicker.value,
    //   countryCode: countryCode.value,
    // });
    // const datafeed = ["US", "CN"].includes(countryCode.value) ? BitgetDatafeed : ["JP"].includes(countryCode.value) ? JpDatafeed : Datafeed;

    const widgetOptions = {
      debug: false,
      symbol: transformTicker.value,
      datafeed: datafeed.default,
      interval: "1h",
      container: chartContainer.value,
      library_path: props.libraryPath,
      locale: locale.value,
      disabled_features: ["use_localstorage_for_settings"],
      enabled_features: [
        "create_volume_indicator_by_default",
        // "header_saveload",
        // "study_templates",
      ],
      // charts_storage_url: '',
      // charts_storage_url: "https://tradingview-prod.blocksquare.tech/api",
      charts_storage_api_version: "v1",
      client_id: "tryex",
      user_id: user_ref_id.value,
      auto_save_delay: 3,
      fullscreen: false,
      autosize: true,
      theme: theme.value,
      time_frames: [],
      // load_last_chart: false,
    };

    chartWidget = new window.TradingView.widget(widgetOptions);

    await new Promise((resolve, reject) => {
      chartWidget.onChartReady(() => {
        // isChartReady = true;
        try {
          chartWidget.applyOverrides({
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            locale: locale.value,
          });
          if (user_ref_id.value && user_ref_id.value != "0") {
            // chartWidget.getSavedCharts(async (list) => {
            //   await setLastChart(list);
            // });

            chartWidget.headerReady().then(() => {
              // chartWidget.subscribe("onAutoSaveNeeded", () => {
              //   chartWidget.saveChartToServer(
              //     () => console.log("Chart saved."),
              //     () => console.error("Save failed."),
              //     {
              //       chartName: transformTicker.value,
              //       defaultChartName: transformTicker.value,
              //     }
              //   );
              // });
            });
            chartWidget
              .activeChart()
              .onSymbolChanged()
              .subscribe(null, () => {
                // console.log(chartWidget.activeChart().symbolExt().symbol);
                // transformTicker.value = chartWidget.activeChart().symbolExt().symbol
                let symbol = chartWidget.activeChart().symbolExt().symbol.split("/")[0];
                // console.log(symbol);

                store.commit("transact/setTicker", symbol);
                // 약간의 지연을 주어 차트가 완전히 준비되도록 함
                // setTimeout(() => {
                //   if (chartWidget && chartWidget.activeChart()) {
                //     setAllDrawings();
                //   }
                // }, 100);
                // store.commit("transact/setTicker", ticker.value);
              });
          }
          resolve();
        } catch (err) {
          reject(err);
        }
      });
    });
  } catch (error) {
    console.error("Error in setTV:", error);
    throw error;
  }
}

async function setAllDrawings() {
  if (!chartWidget || !chartWidget.activeChart) {
    // console.log("Chart not ready for drawings");
    return;
  }

  try {
    const activeChart = chartWidget.activeChart();
    if (!activeChart) {
      // console.log("Active chart not available");
      return;
    }

    const resetDrawings = async () => {
      // console.log("resetDrawings", drawings);
      if (drawings.length > 0) {
        for (const drawing of drawings) {
          if (drawing) {
            try {
              await activeChart.removeEntity(drawing);
            } catch (e) {
              // console.log("Failed to remove drawing:", e);
            }
          }
        }
        drawings = [];
      }
    };
    await resetDrawings();

    if (positions.value.length > 0) {
      positions.value.forEach((position) => {
        let entryLine = setPositionLine(position, "entry");
        let liqLine = setPositionLine(position, "liq");
        if (entryLine) drawings.push(entryLine);
        if (liqLine) drawings.push(liqLine);
      });
    }
    if (openOrderList.value.length > 0) {
      openOrderList.value.forEach((order) => {
        let orderLine = setPositionLine(order, "order");
        if (orderLine) drawings.push(orderLine);
      });
    }
    // console.log("drawings", drawings);
    if (drawings.length > 0) {
      // console.log("drawings", drawings);
      try {
        activeChart.bringToFront(drawings);
      } catch (e) {
        // console.log("Failed to bring drawings to front:", e);
      }
    }
  } catch (error) {
    console.error("Error in setAllDrawings:", error);
  }
}

const calculatePNL = (data) => {
  let pnlAmount = 0;
  let pnlPercentage = 0;
  if (data.side === "buy") {
    // 롱 포지션
    pnlAmount = (lastPrice.value - data.entry_price) * data.amount;
    pnlPercentage = ((lastPrice.value - data.entry_price) / data.entry_price) * 100 * 5;
  } else {
    // 숏 포지션
    pnlAmount = (data.entry_price - lastPrice.value) * data.amount;
    pnlPercentage = ((data.entry_price - lastPrice.value) / data.entry_price) * 100 * 5;
  }
  return {
    pnlAmount,
    pnlPercentage,
  };
};

function setOrderText(data, type) {
  if (type === "order") {
    if (data.type === "tp") {
      return "Take Profit Trigger >= " + data.tp + " | " + "Amt:" + data.amount;
    } else if (data.type === "sl") {
      return "Stop Loss Trigger <= " + data.sl + " | " + "Amt:" + data.amount;
    } else {
      return `${
        String(data.type).charAt(0).toUpperCase() + String(data.type).slice(1)
      }: ${data.price} | Amt: ${Number(data.amount).toFixed(4)}`;
    }
  }
}

function setOrderLineColor(data, type) {
  if (type === "order") {
    if (data.type === "tp") {
      return "#4CAF50";
    } else if (data.type === "sl") {
      return "#FF0000";
    } else {
      return data.side === "buy" ? "#4CAF50" : "#FF0000";
    }
  }
}

const setPositionLine = (data, type = "entry") => {
  // 차트 위젯 상태 체크
  if (!chartWidget || !chartWidget.activeChart) {
    // console.log("Chart widget not ready");
    return null;
  }

  try {
    const activeChart = chartWidget.activeChart();
    if (!activeChart) {
      // console.log("Active chart not available");
      return null;
    }

    let drawingList = [
      {
        type: "entry",
        text: `PNL: ${calculatePNL(data).pnlAmount.toFixed(4)} | Size: ${data.size}`,
        lineColor: data.side === "buy" ? "#4CAF50" : "#FF0000",
        value: data.entry_price,
      },
      {
        type: "liq",
        text: `Liq. ${data.liq_price}`,
        lineColor: "#FF693D",
        value: data.liq_price,
      },
      {
        type: "order",
        text: setOrderText(data, type),
        lineColor: setOrderLineColor(data, type),
        value: data.price,
      },
    ];

    let drawing = drawingList.find((d) => d.type === type);
    if (!drawing) return null;

    let { text, lineColor, value } = drawing;

    return activeChart.createShape(
      {
        price: Number(value),
      },
      {
        shape: "horizontal_line",
        text: text,
        lock: true,
        overrides: {
          linecolor: lineColor,
          linestyle: 1,
          textcolor: theme.value === "Dark" ? "#fff" : "#000",
          fontsize: 13,
          backgroundColor: lineColor,
          backgroundTransparency: 1,
          borderColor: lineColor,
          fixedSize: true,
          drawBorder: true,
          borderWidth: 1,
          alignLabels: true,
          horzLabelsAlign: "left",
          vertLabelsAlign: "middle",
          showLabel: true,
        },
      }
    );
  } catch (error) {
    console.error("Error in setPositionLine:", error);
    return null;
  }
};

// 💾 마지막 차트 불러오기
const setLastChart = (list) => {
  chartWidget.chart().setSymbol(transformTicker.value);

  const filtered = list
    .filter((item) => item.name.startsWith("Binance:"))
    .filter((item) => item.name === transformTicker.value);

  if (filtered.length > 0) {
    const latest = filtered.reduce((a, b) => (a.id > b.id ? a : b));
    chartWidget.loadChartFromServer(latest);
  }
};

// 👀 watch - 심볼 변경 & 다국어
watch(
  () => ticker.value,
  async () => {
    // console.log("ticker changed", ticker.value);
    // if (!isChartReady) {
    //   return;
    // }

    // 기존 drawings 먼저 제거
    if (drawings.length > 0) {
      drawings.forEach((drawing) => {
        if (drawing && chartWidget.activeChart()) {
          try {
            chartWidget.activeChart().removeEntity(drawing);
          } catch (e) {
            // console.log("Failed to remove drawing:", e);
          }
        }
      });
      drawings = [];
    }

    // 심볼 변경
    if (chartWidget && transformTicker.value) {
      chartWidget.chart().setSymbol(transformTicker.value);
    }
    // 심볼 변경이 완료된 후 새로운 drawings 생성
    chartWidget
      .activeChart()
      .onSymbolChanged()
      .subscribe(null, () => {
        // 약간의 지연을 주어 차트가 완전히 준비되도록 함
        setTimeout(() => {
          if (chartWidget && chartWidget.activeChart()) {
            setAllDrawings();
          }
        }, 100);
      });
  }
);

watch(locale, async () => {
  if (chartWidget) {
    await setTV(); // locale 변경 시 차트 재셋팅
  }
});

watch(lastPrice, async (newVal, oldVal) => {
  if (chartWidget && newVal !== oldVal && changeDrawings.value) {
    chartWidget.onChartReady(() => {
      // console.log("lastPrice", newVal);
      if (positions.value.length > 0 || openOrderList.value.length > 0) {
        setAllDrawings();
      }
    });
  }
});

watch(theme, async (newVal, oldVal) => {
  // console.log("theme", newVal);

  if (chartWidget) {
    if (chartWidget) {
      await setTV(); // locale 변경 시 차트 재셋팅
    }
  }
});

watch(positions, async (newVal, oldVal) => {
  // console.log("positions", newVal);
  if (chartWidget) {
    chartWidget.onChartReady(() => {
      setAllDrawings();
    });
  }
});
watch(openOrderList, async (newVal, oldVal) => {
  // console.log("openOrderList", newVal);
  if (chartWidget) {
    chartWidget.onChartReady(() => {
      setAllDrawings();
    });
  }
});

let drawingInterval = null;

// 📦 마운트 시 실행
onMounted(async () => {
  try {
    emit("mounted");
    // await store.dispatch("transact/fetchCoinList");

    // 차트 라이브러리 로드
    await loadChartingLibrary();

    // DOM이 완전히 준비될 때까지 대기
    await nextTick();
    await new Promise((resolve) => setTimeout(resolve, 500));

    // 차트 컨테이너 준비 확인
    if (!chartContainer.value || !chartContainer.value.parentNode) {
      console.error("Chart container not ready:", {
        containerExists: !!chartContainer.value,
        parentExists: !!chartContainer.value?.parentNode,
      });
      return;
    }

    // countryCode와 ticker 변경 감시
    watch(
      [countryCode, transformTicker],
      async (
        [newCountryCode, newTransformTicker],
        [oldCountryCode, oldTransformTicker]
      ) => {
        if (
          newCountryCode &&
          newTransformTicker &&
          (newCountryCode !== oldCountryCode || newTransformTicker !== oldTransformTicker)
        ) {
          try {
            await setTV();
          } catch (error) {
            console.error("Error in chart update:", error);
          }
        }
      },
      { immediate: true }
    );

    // 기타 인터벌 설정
    drawingInterval = setInterval(() => {
      changeDrawings.value = !changeDrawings.value;
    }, 2000);
  } catch (error) {
    console.error("Error in component mount:", error);
  }
});

onUnmounted(() => {
  chartWidget?.remove();
  chartWidget = null;
  clearInterval(drawingInterval);
});
</script>

<style lang="scss" scoped>
/* @use "@/assets/scss/common/main.scss" as main; */

.TVChartContainer {
  height: 630px;
  border-radius: 10px;
  overflow: hidden;
}
@media screen and (max-width: 1400px) and (min-width: 769px) {
  .TVChartContainer {
    height: 560px;
  }
}
@media screen and (max-width: 768px) {
  .TVChartContainer {
    height: 450px;
  }
}
</style>
